import HEADER_PATHS from '~/components/header/header-paths.js';
import { isCasinoGamePath } from '~/utils/pathname';
import {
	ROUTE_CASINO_EXPLORE,
	ROUTE_CASINO_ALL_GAMES,
	ROUTE_CASINO_SEARCH,
	ROUTE_CASINO_SUBMENU,
	ROUTE_CAMPAIGN_REGISTER,
	ROUTE_LIVE_CASINO_EXPLORE,
	ROUTE_LIVE_CASINO_SEARCH,
	ROUTE_LIVE_CASINO_SUBMENU,
	ROUTE_LIVE_CASINO_ALL_GAMES,
	ROUTE_LIVE_CASINO_MY_GAMES_LOVED,
	ROUTE_CASINO_JACKPOT,
	ROUTE_CASINO_MY_GAMES_LOVED,
	ROUTE_CASINO_MY_GAMES_RECENT,
	CASINO_PATHS,
	CASINO_MY_GAMES_PATHS,
	ROUTE_TVBET
} from '~/components/router/constants.js';
import { ROUTE_WE_SPIN_GAME_VIEW } from '~/components/we-spin/new-flow/constants/we-spin-route-constants.js';

const locationHasActiveCategory = (location = window.location) => !!location.state?.categoryId;

const locationHasActiveSubmenu = (location = window.location) =>
	[ROUTE_CASINO_SUBMENU, ROUTE_LIVE_CASINO_SUBMENU].some(route => location.pathname.includes(route));

const locationHasAllGames = (location = window.location) =>
	[ROUTE_CASINO_ALL_GAMES, ROUTE_LIVE_CASINO_ALL_GAMES].some(route => location.pathname.includes(route));

const locationHasMyGames = (location = window.location) =>
	[...CASINO_MY_GAMES_PATHS, ROUTE_LIVE_CASINO_MY_GAMES_LOVED].some(route => location.pathname.includes(route));

const locationHasHeader = (location = window.location, isDesktop) => {
	if (isCasinoGamePath(location.pathname) && !isDesktop) {
		return false;
	}
	if (location.pathname.startsWith(ROUTE_CAMPAIGN_REGISTER)) {
		return false;
	}

	switch (location.pathname) {
		case '/':
			return false;
		default:
			return true;
	}
};

const locationHasSmallerBrandingHeader = (location = window.location, globalNavigationEnabled) => {
	if (!globalNavigationEnabled) {
		return true;
	}

	const isSubmenuRouteActive = locationHasActiveSubmenu(location);
	const isCasinoCategoryActive = locationHasActiveCategory(location);
	const isAllGamesActive = locationHasAllGames(location);
	const isMyGamesActive = locationHasMyGames(location);

	// hide the branding header on top of the top navigation when there is a submenu or category page is opened
	return !(isSubmenuRouteActive || isCasinoCategoryActive || isAllGamesActive || isMyGamesActive);
};

const locationHasTopNav = (location = window.location) => {
	switch (location.pathname) {
		case '/':
			return false;
		default:
			return true;
	}
};

const locationForceAbsoluteHeader = (location = window.location) => {
	if (location.pathname.startsWith(ROUTE_TVBET)) {
		return true;
	}

	return false;
};

const locationHasSubHeader = (location = window.location, isDesktop) => {
	if (isCasinoGamePath(location.pathname) && !isDesktop) {
		return false;
	}

	if (location.pathname.startsWith(ROUTE_CAMPAIGN_REGISTER)) {
		return false;
	}

	if (location.pathname.startsWith(ROUTE_WE_SPIN_GAME_VIEW)) {
		return false;
	}

	switch (location.pathname) {
		case '/':
			return false;
		case '/responsible-gaming':
			return false;
		case '/privacy_policy':
			return false;
		case '/terms-and-conditions':
			return false;
		case '/about':
			return false;
		default:
			return true;
	}
};

const locationForceAbsoluteSubHeader = (location = window.location) => {
	if (location.pathname.startsWith(ROUTE_TVBET)) {
		return true;
	}

	return false;
};

export const locationHasSmallSubHeader = (location = window.location) =>
	CASINO_PATHS.concat(CASINO_MY_GAMES_PATHS).some(path => location.pathname === path);

export const locationHasHeroHeader = (location = window.location) =>
	[
		ROUTE_LIVE_CASINO_EXPLORE,
		ROUTE_LIVE_CASINO_SEARCH,
		ROUTE_LIVE_CASINO_ALL_GAMES,
		ROUTE_LIVE_CASINO_MY_GAMES_LOVED,
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_JACKPOT,
		ROUTE_CASINO_MY_GAMES_LOVED,
		ROUTE_CASINO_SEARCH,
		ROUTE_CASINO_MY_GAMES_RECENT,
		ROUTE_CASINO_SUBMENU,
		ROUTE_LIVE_CASINO_SUBMENU
	].some(route => location.pathname.includes(route));

const locationHasProductNav = (location = window.location) => {
	if (isCasinoGamePath(location.pathname) || location.pathname.startsWith('/we-spin/game-view')) {
		return false;
	}

	const hasProd = HEADER_PATHS.filter(path => location.pathname.startsWith(path));
	return hasProd.length > 0;
};

const locationHasSearchBar = (location, isDesktop, isCasinoMaintenanceEnabled, globalNavigationEnabled) => {
	if (isDesktop) {
		return false;
	}

	const casinoRoutes = [
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_MY_GAMES_LOVED,
		ROUTE_CASINO_MY_GAMES_RECENT,
		ROUTE_CASINO_SEARCH
	];

	const liveCasinoRoutes = [
		ROUTE_LIVE_CASINO_EXPLORE,
		ROUTE_LIVE_CASINO_ALL_GAMES,
		ROUTE_LIVE_CASINO_MY_GAMES_LOVED,
		ROUTE_LIVE_CASINO_SEARCH
	];

	if (isCasinoMaintenanceEnabled && casinoRoutes.includes(location.pathname)) {
		return false;
	}

	const isSubmenuRouteActive = locationHasActiveSubmenu(location);

	const combinedCasinoRoutes = [...casinoRoutes, ...liveCasinoRoutes];

	if (globalNavigationEnabled) {
		const isAllGamesActive = locationHasAllGames(location);
		const isMyGamesActive = locationHasMyGames(location);

		// TODO: framer-motion & useScroll related issue
		// if (locationHasActiveCategory(location) || isSubmenuRouteActive || isAllGamesActive || isMyGamesActive) {
		// locationHasActiveCategory is not taken into account since conditional rendering causes useScroll hook to detect
		// DOM mutations as user initiated scrolls, might we can update framer-motion to motion and see if the issue is
		// still valid or not. for now when we launch casino categories we still render the submenus and search bar even tough
		// we don't need. but with the help of z-index and fixed positioning we can hide that menu
		if (isSubmenuRouteActive || isAllGamesActive || isMyGamesActive) {
			return false;
		}
	}

	return combinedCasinoRoutes.includes(location.pathname) || isSubmenuRouteActive;
};

export {
	locationHasHeader,
	locationHasSmallerBrandingHeader,
	locationHasSubHeader,
	locationHasProductNav,
	locationHasSearchBar,
	locationForceAbsoluteHeader,
	locationForceAbsoluteSubHeader,
	locationHasTopNav
};
